"use client"

import type { ComponentPropsWithoutRef, PropsWithChildren, ReactNode } from "react"
import { createContext, forwardRef, useContext, useEffect, useMemo, useState } from "react"
import { atom, useAtom } from "jotai"
import { last } from "lodash-es"

import { cn } from "../lib/utils"
import {
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandItem as CommandItemBase,
  CommandList,
} from "../shadcnBase/ShadcnCommand"
import { AixKeyboardShortcutKeys } from "./AixKeyboardShortcut"

export const commandBarOpenAtom = atom(false)
export const globalCmdBarInstanceAtom = atom<string[]>([])

export const commandBarIconCn = "mr-2 !h-[14px] !w-[14px]"

const CommandBarSearchContext = createContext("")
const CommandBarAppearanceContext = createContext<"default" | "polished">("default")

export const useCommandBarSearch = () => useContext(CommandBarSearchContext)

type AixCommandItemProps = Pick<ComponentPropsWithoutRef<typeof CommandItemBase>, "onSelect" | "value" | "disabled"> & {
  icon?: ReactNode
  name?: string
  shortCutKeys?: string[]
  className?: string
}

export const AixCommandItem = forwardRef<HTMLDivElement, PropsWithChildren<AixCommandItemProps>>(({ ...p }, _ref) => {
  const appearance = useContext(CommandBarAppearanceContext)

  return (
    <CommandItemBase
      onSelect={p.onSelect}
      value={p.value}
      disabled={p.disabled}
      className={cn("cursor-pointer !py-[6px]", appearance === "polished" && "min-h-10 !py-2", p.className)}
    >
      {p.children || (
        <>
          {p.icon}

          <span className="!text-[13px] text-sm">{p.name}</span>

          {!!p.shortCutKeys && <AixKeyboardShortcutKeys className="ml-auto" keys={p.shortCutKeys} />}
        </>
      )}
    </CommandItemBase>
  )
})

AixCommandItem.displayName = "AixCommandItem"

// AixCommandItem.commandBarIconCn = commandBarIconCn

type AixGlobalCommandBarProps = PropsWithChildren<{ appearance?: "default" | "polished"; name: string }>

export const AixGlobalCommandBar = ({ appearance = "default", name, ...p }: AixGlobalCommandBarProps) => {
  const [commandBarOpen, setCommandBarOpen] = useAtom(commandBarOpenAtom)

  const [globalCmdBarInstances, setGlobalCmdBarInstance] = useAtom(globalCmdBarInstanceAtom)
  const [search, setSearch] = useState("")

  const currentCmdBarInstance = useMemo(() => last(globalCmdBarInstances), [globalCmdBarInstances])

  useEffect(() => {
    if (commandBarOpen) {
      setSearch("")
    }
  }, [commandBarOpen])

  useEffect(() => {
    setGlobalCmdBarInstance((prev) => {
      const iSet = new Set<string>(prev)
      iSet.add(name)
      return Array.from(iSet)
    })

    return () => {
      setGlobalCmdBarInstance((prev) => {
        const iSet = new Set<string>(prev)
        iSet.delete(name)
        return Array.from(iSet)
      })
    }
  }, [name, setGlobalCmdBarInstance])

  if (currentCmdBarInstance !== name) return null

  return (
    <CommandDialog appearance={appearance} open={commandBarOpen} onOpenChange={setCommandBarOpen}>
      <CommandInput value={search} onValueChange={setSearch} placeholder="Type a command or search..." />

      <CommandBarSearchContext.Provider value={search}>
        <CommandBarAppearanceContext.Provider value={appearance}>
          <CommandList className="max-h-[60vh]">
            <CommandEmpty>No results found.</CommandEmpty>
            {p.children}
          </CommandList>
        </CommandBarAppearanceContext.Provider>
      </CommandBarSearchContext.Provider>
    </CommandDialog>
  )
}

AixGlobalCommandBar.displayName = "GlobalCommandBar"
